import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Organization } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import { useUserRole } from '../../hooks/useUserRole';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ContextMenu, { MenuAction } from '../common/ContextMenu';
import OrganizationTable from './OrganizationTable';
import { IMAGES } from '../../constants/assets';
import { POLLING_CONFIG } from '../../config/polling';
import { organizationService, userManagementService } from '../../services';
import GenericSidebar from '../common/GenericSidebar';

// === TYPES ===
interface OrganizationSidebarProps {
  isWide: boolean;
  onToggleWidth: () => void;
}

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  organizationId: string;
}

// === CONSTANTS ===
const SIDEBAR_WIDTHS = {
  compact: 426,
  wide: 800
} as const;

// === SUB-COMPONENTS ===
interface FilterControlsProps {
  searchTerm: string;
  statusFilter: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusFilterChange: (event: SelectChangeEvent) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange
}) => {
  const { t } = useTranslation();
  const { user, isAdminInForeignOrganization } = useUserRole();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {/* Admin Foreign Organization Warning Banner */}


      <TextField
        size="small"
        placeholder={t('admin:sidebar.searchPlaceholder')}
        value={searchTerm}
        onChange={onSearchChange}
        data-testid="admin-org-sidebar-search-input"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ width: '100%' }}
      />

      <FormControl size="small" sx={{ width: '100%' }}>
        <InputLabel>{t('admin:sidebar.status')}</InputLabel>
        <Select
          value={statusFilter}
          label={t('admin:sidebar.status')}
          onChange={onStatusFilterChange}
          data-testid="admin-org-sidebar-status-filter-select"
        >
          <MenuItem value="all">{t('admin:sidebar.statusAll')}</MenuItem>
          <MenuItem value="active">{t('admin:sidebar.statusActive')}</MenuItem>
          <MenuItem value="inactive">{t('admin:sidebar.statusInactive')}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

interface AddOrganizationDialogProps {
  open: boolean;
  organizationName: string;
  organizationEmail: string;
  organizationDescription: string;
  onClose: () => void;
  onSave: () => void;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
}

const AddOrganizationDialog: React.FC<AddOrganizationDialogProps> = ({
  open,
  organizationName,
  organizationEmail,
  organizationDescription,
  onClose,
  onSave,
  onNameChange,
  onEmailChange,
  onDescriptionChange
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: 1
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: '1.5rem',
        fontWeight: 400,
        color: 'primary.dark',
        pb: 2
      }}>
        {t('admin:createDialog.title')}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('admin:createDialog.description')}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          label={t('admin:createDialog.nameLabel')}
          value={organizationName}
          onChange={(e) => onNameChange(e.target.value)}
          variant="outlined"
          placeholder={t('admin:createDialog.namePlaceholder')}
          data-testid="admin-org-create-name-input"
          sx={{ mb: 2 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSave();
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
        />

        <TextField
          fullWidth
          label={t('admin:createDialog.emailLabel')}
          type="email"
          value={organizationEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          variant="outlined"
          placeholder={t('admin:createDialog.emailPlaceholder')}
          data-testid="admin-org-create-email-input"
          sx={{ mb: 2 }}
          required
        />

        <TextField
          fullWidth
          label={t('admin:createDialog.descriptionLabel')}
          value={organizationDescription}
          onChange={(e) => onDescriptionChange(e.target.value)}
          variant="outlined"
          placeholder={t('admin:createDialog.descriptionPlaceholder')}
          data-testid="admin-org-create-description-input"
          multiline
          rows={3}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <span
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            gap: 4,
            color: 'primary.dark',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1rem'
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={onClose}
            data-testid="admin-org-create-dialog-cancel-button"
          >
            <CloseIcon sx={{ fontSize: '1.4rem' }} />
            {t('admin:createDialog.cancel')}
          </Box>
        </span>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!organizationName.trim() || !organizationEmail.trim()}
          data-testid="admin-org-create-submit-button"
          sx={{
            textTransform: 'none',
            flex: 1
          }}
        >
          {t('admin:createDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// === MAIN COMPONENT ===
const OrganizationSidebar: React.FC<OrganizationSidebarProps> = ({ isWide, onToggleWidth }) => {
  // === HOOKS ===
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user, isAdminInForeignOrganization } = useUserRole();
  const location = useLocation();

  // === CORE STATE ===
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // === FILTER/SORT STATE ===
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // === SELECTION STATE ===
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // === DIALOG STATE ===
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [isAddOrganizationDialogOpen, setIsAddOrganizationDialogOpen] = useState(false);
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [newOrganizationEmail, setNewOrganizationEmail] = useState('');
  const [newOrganizationDescription, setNewOrganizationDescription] = useState('');

  // === EVENT HANDLERS ===

  // Filter/Search Handlers
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  }, []);

  // Selection Handlers
  const handleSelectItem = useCallback((event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    event.stopPropagation();
    const newSelectedItems = event.target.checked
      ? [...selectedItems, id]
      : selectedItems.filter(item => item !== id);
    setSelectedItems(newSelectedItems);
  }, [selectedItems]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedItems(organizations.map(org => org._id));
    } else {
      setSelectedItems([]);
    }
  }, [organizations]);

  // Context Menu Handlers
  const handleContextMenu = (event: React.MouseEvent, organizationId: string) => {
    event.preventDefault();
    setSelectedRowId(organizationId);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      organizationId
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedRowId(null);
  };

  // Action Handlers
  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    const selectedOrgs = organizations.filter(org => selectedItems.includes(org._id));
    const inactiveOrgs = selectedOrgs.filter(org => org.status === 'inactive');
    const activeOrgs = selectedOrgs.filter(org => org.status === 'active');

    if (activeOrgs.length > 0) {
      showNotification(
        t('admin:messages.activeOrgsCannotDelete', { count: activeOrgs.length }),
        'warning'
      );
      return;
    }

    if (inactiveOrgs.length === 0) {
      showNotification(t('admin:messages.noInactiveOrgsToDelete'), 'warning');
      return;
    }

    const confirmMessage = t('admin:messages.confirmDeleteOrgs', { count: inactiveOrgs.length });

    if (window.confirm(confirmMessage)) {
      try {
        const inactiveIds = inactiveOrgs.map(org => org._id);
        await organizationService.deleteMultipleOrganizations(inactiveIds);
        showNotification(t('admin:messages.orgsDeletedSuccess', { count: inactiveOrgs.length }), 'success');
        setOrganizations(prev => prev.filter(org => !inactiveIds.includes(org._id)));
        setSelectedItems([]);
      } catch (error) {
        console.error('Error deleting organizations:', error);
        showNotification(t('admin:messages.orgsDeleteError'), 'error');
      }
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedItems.length === 0) return;

    const activeItems = selectedItems.filter(id => {
      const org = organizations.find(o => o._id === id);
      return org?.status === 'active';
    });

    if (activeItems.length === 0) {
      showNotification(t('admin:messages.noActiveOrgsSelected'), 'warning');
      return;
    }

    const confirmMessage = t('admin:messages.confirmDeactivateOrgs', { count: activeItems.length });

    if (window.confirm(confirmMessage)) {
      try {
        for (const orgId of activeItems) {
          await organizationService.deactivateOrganization(orgId);
        }
        showNotification(t('admin:messages.orgsDeactivatedSuccess', { count: activeItems.length }), 'success');

        setOrganizations(prev => prev.map(org =>
          activeItems.includes(org._id) ? { ...org, status: 'inactive' } : org
        ));
        setSelectedItems([]);
      } catch (error) {
        console.error('Error deactivating organizations:', error);
        showNotification(t('admin:messages.orgsDeactivateError'), 'error');
      }
    }
  };

  const handleBulkActivate = async () => {
    if (selectedItems.length === 0) return;

    const inactiveItems = selectedItems.filter(id => {
      const org = organizations.find(o => o._id === id);
      return org?.status === 'inactive';
    });

    if (inactiveItems.length === 0) {
      showNotification(t('admin:messages.noInactiveOrgsSelected'), 'warning');
      return;
    }

    const confirmMessage = t('admin:messages.confirmActivateOrgs', { count: inactiveItems.length });

    if (window.confirm(confirmMessage)) {
      try {
        for (const orgId of inactiveItems) {
          await organizationService.activateOrganization(orgId);
        }
        showNotification(t('admin:messages.orgsActivatedSuccess', { count: inactiveItems.length }), 'success');

        setOrganizations(prev => prev.map(org =>
          inactiveItems.includes(org._id) ? { ...org, status: 'active' } : org
        ));
        setSelectedItems([]);
      } catch (error) {
        console.error('Error activating organizations:', error);
        showNotification(t('admin:messages.orgsActivateError'), 'error');
      }
    }
  };

  // Add Organization Dialog Handlers
  const handleAddOrganization = () => {
    setIsAddOrganizationDialogOpen(true);
  };

  const handleSaveNewOrganization = async () => {
    if (!newOrganizationName.trim()) {
      showNotification(t('admin:messages.nameRequired'), 'error');
      return;
    }

    if (!newOrganizationEmail.trim()) {
      showNotification(t('admin:messages.emailRequired'), 'error');
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newOrganizationEmail.trim())) {
      showNotification(t('admin:messages.emailInvalid'), 'error');
      return;
    }

    try {
      const createRequest = {
        name: newOrganizationName.trim(),
        email: newOrganizationEmail.trim(),
        description: newOrganizationDescription.trim()
      };

      const result = await organizationService.createOrganization(createRequest);
      showNotification(t('admin:messages.createSuccess'), 'success');

      if (result.data) {
        const newOrg: Organization = {
          _id: result.data._id,
          name: result.data.name,
          description: result.data.description || '',
          users: result.data.users || [],
          serviceProviders: result.data.serviceProviders || [],
          createdAt: result.data.createdAt,
          updatedAt: result.data.createdAt,
          status: 'inactive',
          createdBy: 'admin'
        };
        setOrganizations(prev => [...prev, newOrg]);
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Organisation:', error);
      showNotification(t('admin:messages.createError'), 'error');
    }

    setIsAddOrganizationDialogOpen(false);
    setNewOrganizationName('');
    setNewOrganizationEmail('');
    setNewOrganizationDescription('');
  };

  const handleCancelAddOrganization = () => {
    setIsAddOrganizationDialogOpen(false);
    setNewOrganizationName('');
    setNewOrganizationEmail('');
    setNewOrganizationDescription('');
  };

  // Admin Organization Assignment Handler
  const handleAssignAdminToOrganization = async (organizationId: string, organizationName: string) => {
    const confirmMessage = `Möchten Sie sich der Organisation "${organizationName}" zuweisen?\n\nSie werden von Ihrer aktuellen Organisation zu "${organizationName}" wechseln.`;

    if (window.confirm(confirmMessage)) {
      try {
        await userManagementService.assignAdminToOrganization(organizationId);
        showNotification(`Erfolgreich der Organisation "${organizationName}" zugewiesen`, 'success');

        // Seite neu laden um die Änderungen zu reflektieren
        window.location.reload();
      } catch (error) {
        console.error('Fehler bei der Admin-Organisationszuweisung:', error);
        showNotification(t('admin:messages.assignmentError'), 'error');
      }
    }
  };

  // === CONTEXT MENU ACTIONS ===
  const getContextMenuActions = useMemo(() => (organizationId: string): MenuAction[] => {
    const organization = organizations.find(org => org._id === organizationId);
    if (!organization) return [];

    const isActive = organization.status === 'active';
    const isInactive = organization.status === 'inactive';

    const actions: MenuAction[] = [
      {
        label: 'Verwalten',
        icon: <img src={IMAGES.NAVBAR_UBERSICHT} alt="Manage" />,
        onClick: () => {
          navigate(`/admin/${organizationId}`);
          handleCloseContextMenu();
        }
      },
      {
        label: 'Zuweisen',
        icon: <img src={IMAGES.EDIT} alt="Assign" />,
        onClick: () => {
          handleAssignAdminToOrganization(organizationId, organization.name);
          handleCloseContextMenu();
          navigate(`/dienstleister`);
        }
      },
    ];

    if (isActive) {
      actions.push({
        label: 'Deaktivieren',
        icon: <img src={IMAGES.EDIT} alt="Deactivate" />,
        onClick: async () => {
          if (window.confirm(`Möchten Sie die Organisation "${organization.name}" deaktivieren?`)) {
            try {
              await organizationService.deactivateOrganization(organizationId);
              showNotification(t('admin:messages.deactivateSuccess'), 'success');
              setOrganizations(prev => prev.map(org => org._id === organizationId ? { ...org, status: 'inactive' } : org));
            } catch (err) {
              showNotification(t('admin:messages.deactivateError'), 'error');
            }
          }
          handleCloseContextMenu();
        }
      });
    }

    if (isInactive) {
      actions.push({
        label: 'Aktivieren',
        color: '#4CAF50',
        icon: <img src={IMAGES.EDIT} alt="Activate" />,
        onClick: async () => {
          if (window.confirm(`Möchten Sie die Organisation "${organization.name}" wieder aktivieren?`)) {
            try {
              await organizationService.activateOrganization(organizationId);
              showNotification(t('admin:messages.activateSuccess'), 'success');
              setOrganizations(prev => prev.map(org => org._id === organizationId ? { ...org, status: 'active' } : org));
            } catch (err) {
              showNotification(t('admin:messages.activateError'), 'error');
            }
          }
          handleCloseContextMenu();
        }
      });
    }

    if (isInactive) {
      actions.push({
        label: 'Endgültig löschen',
        color: '#FF0004',
        icon: <img src={IMAGES.DELETE} alt="Delete" className="preserve-color" />,
        onClick: async () => {
          if (window.confirm(`⚠️ ACHTUNG: Möchten Sie die Organisation "${organization.name}" ENDGÜLTIG löschen?`)) {
            try {
              await organizationService.deleteOrganization(organizationId);
              showNotification(t('admin:messages.permanentlyDeleteSuccess'), 'success');
              setOrganizations(prev => prev.filter(org => org._id !== organizationId));
            } catch (err) {
              showNotification(t('admin:messages.permanentlyDeleteError'), 'error');
            }
          }
          handleCloseContextMenu();
        }
      });
    } else if (isActive) {
      actions.push({
        label: 'Erst deaktivieren zum Löschen',
        color: '#757575',
        icon: <img src={IMAGES.DELETE} alt="Cannot Delete" className="preserve-color" />,
        onClick: () => {
          showNotification(t('admin:messages.mustDeactivateFirst'), 'warning');
          handleCloseContextMenu();
        }
      });
    }

    return actions;
  }, [navigate, showNotification, organizations]);

  // === EFFECTS ===
  useEffect(() => {
    const loadOrganizations = async () => {
      setLoading(true);
      try {
        const orgs = await organizationService.getOrganizations();
        setOrganizations(orgs);
        setError(null);
      } catch (err) {
        setError(t('admin:messages.loadOrgsError'));
        console.error('Error loading organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (!POLLING_CONFIG.ENABLE_POLLING) return;
    const pollOrganizations = async () => {
      try {
        const freshOrganizations = await organizationService.getOrganizations();
        setOrganizations(prevOrgs => {
          if (JSON.stringify(prevOrgs) !== JSON.stringify(freshOrganizations)) {
            return freshOrganizations;
          }
          return prevOrgs;
        });
      } catch (error) {
        console.error('Organization polling error:', error);
      }
    };
    const interval = setInterval(pollOrganizations, POLLING_CONFIG.ADMIN_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const selectedOrgs = organizations.filter(org => selectedItems.includes(org._id));
  const activeCount = selectedOrgs.filter(org => org.status === 'active').length;
  const inactiveCount = selectedOrgs.filter(org => org.status === 'inactive').length;

  return (
    <GenericSidebar
      title={t('admin:sidebar.title')}
      titlePath="/admin"
      isWide={isWide}
      onToggleWidth={onToggleWidth}
      loading={loading}
      error={error}
      filterControls={
        <FilterControls
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
        />
      }
      tableContent={
        <OrganizationTable
          organizations={organizations}
          selectedItems={selectedItems}
          selectedRowId={selectedRowId}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          isWide={isWide}
          onSelectItem={handleSelectItem}
          onSelectAll={handleSelectAll}
          onContextMenu={handleContextMenu}
        />
      }
      actionButtons={
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {selectedItems.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {selectedItems.length} ausgewählt
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <span
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    gap: 4,
                    color: 'primary.dark',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    onClick={() => setSelectedItems([])}
                    data-testid="admin-org-sidebar-cancel-selection-button"
                  >
                    <CloseIcon sx={{ fontSize: '1.4rem' }} />
                    {t('admin:sidebar.cancel')}
                  </Box>
                </span>
                <Button size="small" color="error" startIcon={<DeleteIcon />} variant="outlined" onClick={handleDeleteSelected} disabled={inactiveCount === 0} sx={{ textTransform: 'none', color: inactiveCount === 0 ? '#bdbdbd' : '#FF0004', borderColor: inactiveCount === 0 ? '#bdbdbd' : '#FF0004', fontSize: '0.75rem', minWidth: 'auto', px: 1, flex: 1 }} data-testid="admin-org-sidebar-bulk-delete-button">
                  {inactiveCount > 0 ? t('admin:sidebar.deleteCount', { count: inactiveCount }) : t('admin:sidebar.delete')}
                </Button>
              </Box>
              {(activeCount > 0 || inactiveCount > 0) && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {inactiveCount > 0 && (
                    <Button size="small" variant="outlined" onClick={handleBulkActivate} sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1, color: '#10b981', borderColor: '#10b981', '&:hover': { backgroundColor: '#f0fdf4', borderColor: '#10b981' } }} data-testid="admin-org-sidebar-bulk-activate-button">
                      {t('admin:sidebar.activateCount', { count: inactiveCount })}
                    </Button>
                  )}
                  {activeCount > 0 && (
                    <Button size="small" variant="outlined" onClick={handleBulkDeactivate} sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1, color: '#f59e0b', borderColor: '#f59e0b', '&:hover': { backgroundColor: '#fffbeb', borderColor: '#f59e0b' } }} data-testid="admin-org-sidebar-bulk-deactivate-button">
                      {t('admin:sidebar.deactivateCount', { count: activeCount })}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOrganization} data-testid="admin-org-sidebar-add-button" sx={{ textTransform: 'none', width: '100%' }}>
              {t('admin:sidebar.addOrganization')}
            </Button>
          )}
        </Box>
      }
      dialogs={
        <>
          <ContextMenu
            open={contextMenu !== null}
            onClose={handleCloseContextMenu}
            mouseX={contextMenu?.mouseX || 0}
            mouseY={contextMenu?.mouseY || 0}
            actions={contextMenu ? getContextMenuActions(contextMenu.organizationId) : []}
          />
          <AddOrganizationDialog
            open={isAddOrganizationDialogOpen}
            organizationName={newOrganizationName}
            organizationEmail={newOrganizationEmail}
            organizationDescription={newOrganizationDescription}
            onClose={handleCancelAddOrganization}
            onSave={handleSaveNewOrganization}
            onNameChange={setNewOrganizationName}
            onEmailChange={setNewOrganizationEmail}
            onDescriptionChange={setNewOrganizationDescription}
          />
        </>
      }
    />
  );
};

export default OrganizationSidebar; 